import React, { useEffect, useState } from 'react'
import { CostsScreenValidationResult } from '../../../../../../../@types/validation-types'
import { InsertionFlowScreensProps } from '../../../../../../../@types/props-types'
import { FieldProps } from '../../../../../../../@types/types'
import { parseNumber } from '../../../../../../../services/validation/rules/validationFunctions'
import Costs from '../../Costs'
import PriceField from '../../../basicInfo/fields/priceField/PriceField'
import { PriceFieldType } from '../../../basicInfo/fields/priceField/PriceField'
import AdditionalCosts from '../../fields/additionalCosts/AdditionalCosts'
import HeatingCostsWithToggle from '../../fields/heatingCostsWithToggle/HeatingCostsWithToggle'
import TotalRent from '../../fields/totalRent/TotalRent'
import DepositWithToggle from '../../fields/depositWithToggle/DepositWithToggle'
import Deposit from '../../fields/deposit/Deposit'
import { isWebView } from '../../../../../../../utils/navigation'
import { IconS24EuroCircle24 } from '@is24/cosma-ui-icons'

export type WebViewCostsRentProps = {
  screenTooltipKey?: string
}

const WebViewCostsRent: React.FC<InsertionFlowScreensProps<CostsScreenValidationResult> & WebViewCostsRentProps> = (
  props
) => {
  const showLabelWithTooltip = isWebView()
  const [calculatedTotal, setCalculatedTotal] = useState<number | undefined>(undefined)
  const [isManualTotalRent, setIsManualTotalRent] = useState<boolean>(false)

  // Synchronize isManualTotalRent with manualTotalRent in props
  useEffect(() => {
    if (props.insertionData.manualTotalRent !== undefined && props.insertionData.manualTotalRent !== null) {
      setIsManualTotalRent(true)
    } else {
      setIsManualTotalRent(false)
    }
  }, [props.insertionData.manualTotalRent])
  // Get current values from insertion data
  const baseRentValue = props.insertionData.price ? parseNumber(props.insertionData.price) : 0
  const additionalCostsValue = props.insertionData.additionalCosts ? props.insertionData.additionalCosts : 0
  const heatingCostsValue = props.insertionData.heatingCosts ? parseNumber(props.insertionData.heatingCosts) : 0
  const heatingCostsOption = props.insertionData.heatingCostsOption ?? true
  const manualTotalRent = props.insertionData.manualTotalRent
    ? parseNumber(props.insertionData.manualTotalRent)
    : undefined

  // Calculate total rent based on current values and toggle state
  useEffect(() => {
    if (manualTotalRent !== undefined) {
      setCalculatedTotal(manualTotalRent)
      return
    }

    if (baseRentValue > 0) {
      let total = baseRentValue

      if (additionalCostsValue > 0) {
        total += additionalCostsValue
      }

      // Add heating costs only if toggle is OFF and heating costs are specified
      if (!heatingCostsOption && heatingCostsValue > 0) {
        total += heatingCostsValue
      }
      setCalculatedTotal(total)
    } else {
      setCalculatedTotal(undefined)
    }
  }, [baseRentValue, additionalCostsValue, heatingCostsValue, heatingCostsOption, manualTotalRent])

  return (
    <Costs {...props} headerKey='wizard.screen.costs.mobileNativeHeader' headerIcon={<IconS24EuroCircle24 />}>
      {(costsWrapperProps: FieldProps<CostsScreenValidationResult>) => (
        <div className='grid grid-flex gutter-vertical-l'>
          <div className='grid-item one-whole'>
            <PriceField
              {...costsWrapperProps}
              priceFieldType={PriceFieldType.COLD_RENT}
              wrapperClassName='grid-item one-whole'
              showLabelWithTooltip={showLabelWithTooltip}
            />
            <AdditionalCosts
              {...costsWrapperProps}
              wrapperClassName='grid-item one-whole margin-top-l'
              showLabelWithTooltip={showLabelWithTooltip}
            />

            <HeatingCostsWithToggle {...costsWrapperProps} doReset={props.doReset!} />

            <TotalRent
              {...costsWrapperProps}
              calculatedValue={isManualTotalRent ? undefined : calculatedTotal}
              disabled={false}
              onChange={(screenProperty, value) => {
                if (isManualTotalRent) {
                  props.onChange(screenProperty, value)
                }
              }}
              onFocus={() => {
                if (!isManualTotalRent) {
                  setIsManualTotalRent(true)
                  if (calculatedTotal !== undefined && calculatedTotal !== null) {
                    props.onChange(
                      { screen: costsWrapperProps.screenName, property: 'manualTotalRent' },
                      calculatedTotal
                    )
                  }
                }
              }}
            />

            {showLabelWithTooltip ? (
              <DepositWithToggle {...costsWrapperProps} doReset={props.doReset!} />
            ) : (
              <Deposit {...costsWrapperProps} />
            )}
          </div>
        </div>
      )}
    </Costs>
  )
}

export default WebViewCostsRent
